// resolveInputs.ts — Phase 1 of the mergeFile pipeline (FER-10394).
//
// Owns: base-generation lookup, file-path resolution (rename detection),
// metadata construction, BASE/OURS content fetches (with rename-source
// fallback), and ghost-commit hybrid reconstruction when the base tree is
// unreachable.
//
// Behavioral equivalence claim: for every input combination, the returned
// `ResolvedInputs` (or `earlyExit`) carries the same values as the
// pre-refactor `mergeFile` had at line 442 (just before the snapshot
// decision block).

import { readFile } from "node:fs/promises";
import { join } from "node:path";
import type { GitClient } from "../git/GitClient.js";
import type { LockfileManager } from "../LockfileManager.js";
import type { ConflictMetadata, GenerationRecord, StoredPatch } from "../types.js";
import type { ResolvedInputs } from "./MergePlan.js";

export interface ResolveInputsArgs {
    patch: StoredPatch;
    filePath: string;
    git: GitClient;
    lockManager: LockfileManager;
    outputDir: string;
    isTreeReachable: (treeHash: string) => Promise<boolean>;
    resolveBaseGeneration: (sha: string) => Promise<GenerationRecord | undefined>;
    resolveFilePath: (filePath: string, baseTree: string, currentTree: string) => Promise<string>;
    extractRenameSource: (patchContent: string, filePath: string) => string | null;
    extractFileDiff: (patchContent: string, filePath: string) => string | null;
}

export async function resolveInputs(args: ResolveInputsArgs): Promise<ResolvedInputs> {
    const {
        patch,
        filePath,
        git,
        lockManager,
        outputDir,
        isTreeReachable,
        resolveBaseGeneration,
        resolveFilePath,
        extractRenameSource,
        extractFileDiff,
    } = args;

    const baseGen = await resolveBaseGeneration(patch.base_generation);
    if (!baseGen) {
        return {
            resolvedPath: filePath,
            metadata: {
                patchId: patch.id,
                patchMessage: patch.original_message,
                baseGeneration: patch.base_generation,
                currentGeneration: lockManager.read().current_generation,
            },
            base: null,
            ours: null,
            oursPath: join(outputDir, filePath),
            renameSourcePath: undefined,
            ghostTheirs: null,
            earlyExit: { status: "skipped", reason: "base-generation-not-found" },
        };
    }

    const lock = lockManager.read();
    const currentGen = lock.generations.find((g) => g.commit_sha === lock.current_generation);
    const currentTreeHash = currentGen?.tree_hash ?? baseGen.tree_hash;
    const resolvedPath = await resolveFilePath(filePath, baseGen.tree_hash, currentTreeHash);

    const metadata: ConflictMetadata = {
        patchId: patch.id,
        patchMessage: patch.original_message,
        baseGeneration: patch.base_generation,
        currentGeneration: lock.current_generation,
    };

    let base = await git.showFile(baseGen.tree_hash, filePath);

    let renameSourcePath: string | undefined;
    if (!base) {
        const renameSource = extractRenameSource(patch.patch_content, filePath);
        if (renameSource) {
            base = await git.showFile(baseGen.tree_hash, renameSource);
            renameSourcePath = renameSource;
        }
    }

    const oursPath = join(outputDir, resolvedPath);
    const ours = await readFile(oursPath, "utf-8").catch(() => null);

    let ghostTheirs: string | null = null;

    // Silent-loss signal (INC-866): when BASE can't be read because the
    // base-generation tree is outside the clone window, record it so Phase 2/3
    // can warn rather than silently misclassifying an edited-existing-file
    // patch as `new-file-both` and dropping the customer's edit. We probe
    // reachability whenever BASE is null and we didn't recover it via a rename
    // source (a reachable rename source means the tree WAS reachable).
    let baseTreeUnreachable = false;

    // Ghost-commit reconstruction: when the base tree is unreachable (GC'd
    // after squash merge, shallow clone), reconstruct hybrid BASE and THEIRS
    // from the unified diff's context/removed/added lines matched against OURS.
    if (!base && !renameSourcePath) {
        const treeReachable = await isTreeReachable(baseGen.tree_hash);
        if (!treeReachable) {
            baseTreeUnreachable = true;
            if (ours) {
                const fileDiff = extractFileDiff(patch.patch_content, filePath);
                if (fileDiff) {
                    const { reconstructFromGhostPatch } = await import("../HybridReconstruction.js");
                    const result = reconstructFromGhostPatch(fileDiff, ours);
                    if (result) {
                        base = result.base;
                        ghostTheirs = result.theirs;
                    }
                }
            }
        }
    }

    return {
        resolvedPath,
        metadata,
        base,
        ours,
        oursPath,
        renameSourcePath,
        ghostTheirs,
        // Only meaningful when ghost reconstruction did NOT recover a base; if it
        // did, `base` is non-null and the merge proceeds normally. We still set
        // the flag truthfully (the tree IS unreachable) so Phase 3 can decide.
        baseTreeUnreachable,
    };
}
